import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useEffect, useMemo } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import { useGetPaymentMethods } from "src/api/generic";
import { useGetSalarySalary, usePaySalarySalary } from "src/api/salary/salaries";
import { notificationsState } from "src/store/notifications";
import { APIErrorResponse } from "src/types/generic";
import {
  paymentFormDefaultValues,
  paymentFormSchema,
  PaymentFormValues,
} from "./constants";

const SalaryPayModal = ({
  open,
  onClose,
  onPaid,
  salaryId,
  salaryReference,
}: {
  open: boolean;
  onClose: () => void;
  onPaid: () => void;
  salaryId: number;
  salaryReference?: string;
}) => {
  const { t } = useTranslation("salary");
  const { t: generalT } = useTranslation("general");
  const [notifications, setNotifications] = useRecoilState(notificationsState);

  // APIs
  const { data: salaryRes } = useGetSalarySalary(salaryId, open);
  const detail = salaryRes?.data;
  const { data: pmRes } = useGetPaymentMethods({ page: 1, enabled: open });
  const paymentMethods = pmRes?.data ?? [];
  const { mutate: payMut, isPending } = usePaySalarySalary();

  const remaining = detail?.remaining_amount ?? 0;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PaymentFormValues>({
    mode: "onBlur",
    resolver: yupResolver(paymentFormSchema),
    context: { remaining },
    defaultValues: paymentFormDefaultValues,
  });

  useEffect(() => {
    if (!open) return;
    reset({
      ...paymentFormDefaultValues,
      amount: remaining > 0 ? remaining : 0,
    });
  }, [open, remaining, reset]);

  const title = useMemo(() => {
    const ref = salaryReference ?? detail?.reference ?? "";
    return ref ? `${t("paySalary")} — ${ref}` : t("paySalary");
  }, [t, salaryReference, detail?.reference]);

  const onSubmit: SubmitHandler<PaymentFormValues> = (values) => {
    payMut(
      {
        salaryId,
        payment_method_id: Number(values.payment_method_id),
        amount: Number(values.amount),
        note: values.note?.trim() || undefined,
      },
      {
        onSuccess: () => {
          setNotifications([
            ...notifications,
            { type: "success", message: t("paidSuccessfully") },
          ]);
          onPaid();
        },
        onError: (err: APIErrorResponse) => {
          setNotifications([
            ...notifications,
            {
              type: "error",
              message:
                err?.data?.errors?.[0]?.value ?? generalT("somethingWrong"),
            },
          ]);
        },
      },
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Stack
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        spacing={3}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: "537px" },
          maxHeight: "90vh",
          overflowY: "auto",
          bgcolor: "#FFF",
          border: "1px solid #D0D5DD",
          borderRadius: 1,
          boxShadow: 24,
          p: 3,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" p={0}>
          <Typography color="#101828" fontSize="20px" lineHeight="24px">
            {title}
          </Typography>
          <IconButton onClick={onClose} sx={{ p: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Stack spacing={2}>
          <Typography color="#667085" fontSize={14}>
            {t("remainingAmount")}: <b>{remaining.toFixed(2)}</b>
          </Typography>

          <Controller
            name="payment_method_id"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                size="small"
                select
                label={generalT("paymentMethod")}
                fullWidth
                error={!!errors.payment_method_id}
                helperText={
                  errors.payment_method_id
                    ? t(errors.payment_method_id.message as string)
                    : undefined
                }
              >
                <MenuItem value="">{generalT("paymentMethod")}</MenuItem>
                {paymentMethods.map((pm: any) => (
                  <MenuItem key={pm.id} value={pm.id}>
                    {pm.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <Controller
            name="amount"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                type="number"
                label={generalT("amount")}
                fullWidth
                error={!!errors.amount}
                helperText={
                  errors.amount
                    ? t(errors.amount.message as string)
                    : ""
                }
              />
            )}
          />

          <Controller
            name="note"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                size="small"
                label={generalT("notes")}
                fullWidth
                multiline
                minRows={3}
              />
            )}
          />
        </Stack>

        <Stack spacing={2} direction="row">
          <Button variant="outlined" color="tertiary" fullWidth onClick={onClose}>
            {generalT("cancel")}
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            fullWidth
            loading={isPending}
            disabled={remaining <= 0}
          >
            {generalT("pay")}
          </LoadingButton>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default SalaryPayModal;
